<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { ROLES } from "@/services/acl";
import UserDetailsPanel from "@/components/UserDetailsPanel.vue";
import MediaComplianceRuleWidget from "@/components/MediaComplianceRuleWidget.vue";
import {
  fetchComplaints,
  fetchComplaintsByUser,
  fetchUserById,
  fetchUserPurchases,
  fetchAdmins,
  fetchVerificationAttempts,
  fetchVerificationMedia,
  getMediaUrl,
  updateUser,
  updateUserStatus,
  updateComplaintStatus,
  setComplaintAssignedAdmin,
  addMediaLabel,
  rerunComplaintAi,
  sendUserWarningMessage,
} from "@/services/backofficeClient";

const route = useRoute();
const router = useRouter();
const store = useStore();
const canEditUser = computed(() => store.getters.hasRole(ROLES.USER_EDIT));
const canEditComplaint = computed(() => store.getters.hasRole(ROLES.COMPLAINT_EDIT));
const canEditMedia = computed(() => store.getters.hasRole(ROLES.MEDIA_EDIT));
const canViewPay = computed(() => store.getters.hasRole(ROLES.PAY_INDEX));

const COMPLIANCE_ACTION_REASON = "Compliance";

const isLoading = ref(true);
const errorMessage = ref("");
const userSummary = ref(null);
const complaints = ref([]);
const statusMessage = ref("");
const statusType = ref("success");
const showMediaModal = ref(false);
const currentMediaUrl = ref("");
const currentMediaObjectUrl = ref("");
const verificationAttempts = ref([]);
const selectedUserStatus = ref("");
const userStatusComment = ref(COMPLIANCE_ACTION_REASON);
const selectedComplaintStatus = ref("");
const selectedComplaintStatusComment = ref("");
const labelNoHumanFace = ref(false);
const requireVerification = ref(false);
const sendWarningMessage = ref(false);
const selectedReporterIdsToWarn = ref([]);
const detailsPanelRef = ref(null);
const isApplying = ref(false);
const isLoadingNext = ref(false);
const rerunAiLoadingFor = ref(null);
const statusChangeTarget = ref(null);
const statusChangeComment = ref("");
const isUpdatingStatus = ref(false);

const complaintStatusOptions = ["resolved", "rejected"];
const userStatusOptions = ["active", "blocked", "ghosted"];
const bulkComplaintOptions = ["resolved", "rejected"];
const NEXT_PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Admins (loaded once on mount, kept in memory)
// ---------------------------------------------------------------------------
const admins = ref([]);
const adminsById = computed(() => {
  const map = {};
  admins.value.forEach((admin) => {
    map[admin.id] = admin;
  });
  return map;
});

const loadAdmins = async () => {
  try {
    const payload = await fetchAdmins({ pageSize: 200 });
    const items = Array.isArray(payload) ? payload : payload?.items;
    admins.value = (items || []).map((admin) => ({
      id: admin.id,
      name: admin.name || admin.email || admin.id,
    }));
  } catch (err) {
    console.error("Failed to load admins", err);
  }
};

const adminLabel = (adminId) => {
  if (!adminId) return "Unassigned";
  return adminsById.value[adminId]?.name || adminId;
};

// ---------------------------------------------------------------------------
// Assign / Unassign admin
// ---------------------------------------------------------------------------
const assignMenuOpenFor = ref(null);
const assignLoading = ref(false);
const assignError = ref("");

const toggleAssignMenu = (complaintId) => {
  assignMenuOpenFor.value =
    assignMenuOpenFor.value === complaintId ? null : complaintId;
};

const assignComplaintToAdmin = async (complaint, adminId) => {
  assignMenuOpenFor.value = null;
  assignLoading.value = true;
  assignError.value = "";
  try {
    const updated = await setComplaintAssignedAdmin(complaint.id, adminId);
    const normalized = parseComplaint(updated);
    complaints.value = complaints.value.map((item) =>
      item.id === normalized.id ? normalized : item
    );
  } catch (err) {
    assignError.value = err?.message || "Failed to update assignment.";
  } finally {
    assignLoading.value = false;
  }
};

const defaultSortByForGroup = (value) =>
  value === "reporterUserId" ? "count" : "lastCreatedAt";

const photoAtoms = computed(() => {
  const atoms = Array.isArray(userSummary.value?.atoms) ? userSummary.value.atoms : [];
  return atoms.filter((atom) => atom?.mediaPath && atom.mediaPath.includes("photo"));
});

const parseVerificationAttempt = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ? String(raw.id) : "",
    verificationMediaPaths: Array.isArray(raw.verificationMediaPaths)
      ? raw.verificationMediaPaths.map((path) => (path == null ? "" : String(path))).filter(Boolean)
      : [],
    verificationPhotoPaths: Array.isArray(raw.verificationPhotoPaths)
      ? raw.verificationPhotoPaths.map((path) => (path == null ? "" : String(path))).filter(Boolean)
      : [],
  };
};

const loadVerificationAttempts = async (id) => {
  if (!id) return;
  try {
    const payload = await fetchVerificationAttempts(id);
    const items = Array.isArray(payload) ? payload : payload?.items;
    verificationAttempts.value = (items || [])
      .map((item) => parseVerificationAttempt(item))
      .filter(Boolean);
  } catch (error) {
    console.warn("Failed to fetch verification attempts for complaints", error);
  }
};

const verificationPhotoItems = computed(() => {
  const items = [];
  verificationAttempts.value.forEach((attempt) => {
    const mediaPhotoPaths = attempt.verificationMediaPaths.filter(
      (path) => path.includes("photo") && getMediaUrl(path)
    );
    if (mediaPhotoPaths.length) {
      mediaPhotoPaths.forEach((path, idx) => {
        items.push({
          key: `${attempt.id}-media-${idx}`,
          kind: "media",
          url: getMediaUrl(path),
        });
      });
    } else {
      attempt.verificationPhotoPaths.forEach((_path, idx) => {
        items.push({
          key: `${attempt.id}-legacy-${idx}`,
          kind: "legacy",
          attemptId: attempt.id,
          index: idx,
        });
      });
    }
  });
  return items;
});

const openVerificationPhoto = async (attemptId, index) => {
  if (!attemptId || typeof index !== "number") return;
  try {
    const payload = await fetchVerificationMedia(attemptId, index);
    if (!(payload?.blob instanceof Blob)) {
      statusMessage.value = "Unable to load verification photo. Please try again.";
      statusType.value = "danger";
      return;
    }
    if (currentMediaObjectUrl.value) {
      URL.revokeObjectURL(currentMediaObjectUrl.value);
      currentMediaObjectUrl.value = "";
    }
    const objectUrl = URL.createObjectURL(payload.blob);
    currentMediaObjectUrl.value = objectUrl;
    statusMessage.value = "";
    openMedia(objectUrl);
  } catch (error) {
    console.error("Failed to load verification photo", error);
    statusMessage.value =
      error?.payload?.message || error?.message || "Failed to load verification photo. Please try again.";
    statusType.value = "danger";
  }
};

const userBio = computed(() => {
  const atoms = Array.isArray(userSummary.value?.atoms) ? userSummary.value.atoms : [];
  const bioAtom = atoms.find(
    (atom) => atom?.attribute === "bio" && typeof atom.textValue === "string" && atom.textValue.trim()
  );
  return bioAtom?.textValue || "";
});

const handleMediaComplianceStatus = ({ message, type }) => {
  statusMessage.value = message;
  statusType.value = type;
};

const userId = computed(() => route.params.id);
const groupBy = computed(() =>
  route.meta?.complaintGroup === "reporterUserId"
    ? "reporterUserId"
    : "reportedUserId"
);
const isGroupedByReporter = computed(() => groupBy.value === "reporterUserId");
const statusFilter = computed(() => {
  const value = route.query?.status;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
});
const sortByFilter = computed(() => {
  const value = route.query?.sortBy;
  if (typeof value === "string" && value.trim().length > 0) return value.trim();
  return defaultSortByForGroup(groupBy.value);
});
const assignedAdminIdsFilter = computed(() => {
  const value = route.query?.assignedAdminIds;
  if (value === undefined) return undefined;
  const list = Array.isArray(value) ? value : [value];
  return list
    .filter((v) => typeof v === "string" && v.length > 0)
    .map((v) => (v === "null" ? null : v));
});
const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatStatus = (status) => {
  if (!status) return "--";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const statusButtonClass = (status, currentStatus) => {
  const color =
    status === "resolved" ? "success" : status === "rejected" ? "danger" : "warning";
  const variant = currentStatus === status ? `btn-${color}` : `btn-outline-${color}`;
  return ["btn", "btn-sm", variant, "m-1"];
};

const userStatusButtonClass = (status, currentStatus) => {
  const color =
    status === "active" ? "success" : status === "blocked" ? "danger" : "secondary";
  const variant = currentStatus === status ? `btn-${color}` : `btn-outline-${color}`;
  return ["btn", "btn-xs", variant, "m-1"];
};

const bulkStatusButtonClass = (status, currentStatus) => {
  const color = status === "resolved" ? "success" : "danger";
  const variant = currentStatus === status ? `btn-${color}` : `btn-outline-${color}`;
  return ["btn", "btn-xs", variant, "m-1"];
};

const isStatusLocked = (currentStatus, nextStatus) => {
  const isTerminal = currentStatus === "resolved" || currentStatus === "rejected";
  return isTerminal && nextStatus === "pending";
};

const statusBadgeClass = (status) => {
  const base = ["badge", "text-uppercase", "fw-semibold"];
  if (status === "resolved") return [...base, "bg-success"];
  if (status === "rejected") return [...base, "bg-danger"];
  if (status === "pending") return [...base, "bg-warning", "text-dark"];
  return [...base, "bg-secondary"];
};

const aiDecisionActionLabels = {
  reject: "Reject",
  blockUser: "Block user",
  ghostUser: "Ghost user",
  requestAccountVerification: "Request verification",
  requestFaceProfilePhotos: "Request face photos",
  requestAccountVerificationAndFaceProfilePhotos:
    "Request verification & face photos",
  escalate: "Escalate",
};

const formatAiDecisionAction = (action) => {
  if (!action) return "--";
  return aiDecisionActionLabels[action] || action;
};

const aiDecisionBadgeClass = (action) => {
  const base = ["badge", "text-uppercase", "fw-semibold"];
  if (action === "reject") return [...base, "bg-secondary"];
  if (action === "blockUser") return [...base, "bg-danger"];
  if (action === "ghostUser") return [...base, "bg-dark"];
  if (
    action === "requestAccountVerification" ||
    action === "requestFaceProfilePhotos" ||
    action === "requestAccountVerificationAndFaceProfilePhotos"
  ) {
    return [...base, "bg-info", "text-dark"];
  }
  if (action === "escalate") return [...base, "bg-warning", "text-dark"];
  return [...base, "bg-light", "text-dark", "border"];
};

const safeText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const parseComplianceMatchValue = (value) => {
  if (!value || typeof value !== "object") return null;
  const type = value.type;
  const field = value.field || value.fieldName || value.name || "";
  if (type === "StringValue") {
    const normalizedValue =
      value.value === null || value.value === undefined
        ? ""
        : String(value.value);
    return {
      type: "StringValue",
      field,
      value: normalizedValue,
    };
  }
  if (type === "MediaValue") {
    const media = value.media && typeof value.media === "object" ? value.media : {};
    const mediaPath = media._id || media.path || media.id || media.mediaPath;
    const mediaUserId = media.user ? String(media.user) : "";
    const labels = Array.isArray(media.labels)
      ? media.labels
        .map((label) => {
          if (!label || typeof label !== "object") return null;
          return {
            name: label.label || label.name || label.value || "",
            value: label.value,
            score: label.score,
          };
        })
        .filter(Boolean)
      : [];
    const references = Array.isArray(media.references)
      ? media.references
        .map((reference) => ({
          type: reference?.type || "",
          id: reference?.id ? String(reference.id) : "",
        }))
        .filter((reference) => reference.type && reference.id)
      : [];
    return {
      type: "MediaValue",
      field: field || "media",
      mediaPath,
      mediaUserId,
      mediaType: media.type,
      mediaUrl: mediaPath ? getMediaUrl(mediaPath) : null,
      labels,
      references,
    };
  }
  return null;
};

const mediaReferenceLabel = (reference) => {
  if (!reference) return "";
  if (reference.type === "userAtom") return "Profile";
  if (reference.type === "message") return "Message";
  return reference.type;
};

const mediaReferenceUrl = (reference, ownerUserId) => {
  if (!reference) return null;
  if (reference.type === "userAtom") {
    return `/users/${reference.id}`;
  }
  if (reference.type === "message") {
    const ownerId = ownerUserId ? String(ownerUserId) : "";
    if (!ownerId) return null;
    const query = new URLSearchParams();
    query.append("participantIds[]", ownerId);
    query.append("messageId", reference.id);
    return `/messages?${query.toString()}`;
  }
  return null;
};

const parseComplianceMatchValues = (values) => {
  if (!Array.isArray(values)) return [];
  return values.map((value) => parseComplianceMatchValue(value)).filter(Boolean);
};

const parseStatusLogActor = (actor) => {
  if (!actor || typeof actor !== "object") return "Unknown";
  if (actor.type === "admin") return actor.adminName || actor.adminId || "Admin";
  if (actor.type === "ai") return "AI";
  if (actor.type === "system") return "System";
  return String(actor.type || "Unknown");
};

const parseStatusLog = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      return {
        status: entry.status ? String(entry.status) : "",
        actorLabel: parseStatusLogActor(entry.actor),
        comment: entry.comment ? String(entry.comment) : null,
        changedAt: entry.changedAt || null,
      };
    })
    .filter(Boolean);
};

// insight comes from the API as either a plain string (legacy data)
// or a map of language code -> localized text.
const localizeAiText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    return (
      value[store.getters.preferredLanguage] ||
      value[store.state.tenantConfig?.defaultLanguage] ||
      value.en ||
      Object.values(value)[0] ||
      ""
    );
  }
  return String(value);
};

const parseAiAction = (value) => {
  if (!value || typeof value !== "object") return null;
  const action =
    value.action === null || value.action === undefined
      ? ""
      : String(value.action);
  if (!action) return null;
  return {
    action,
    insight: localizeAiText(value.insight),
  };
};

const formatComplianceLabel = (label) => {
  if (!label || typeof label !== "object") return "";
  const parts = [];
  if (label.name) parts.push(safeText(label.name));
  if (label.value) parts.push(`value: ${safeText(label.value)}`);
  const numericScore = Number(label.score);
  if (!Number.isNaN(numericScore)) {
    const scoreText =
      numericScore >= 0 && numericScore <= 1
        ? `${(numericScore * 100).toFixed(0)}%`
        : numericScore.toFixed(2);
    parts.push(`score: ${scoreText}`);
  }
  return parts.join(" • ");
};

const parseComplaint = (item) => {
  return {
    id: item.id || "",
    reporterType: item.reporterType || "",
    reporterUserId: item.reporterUserId || "",
    reporterUserName: item.reporterUserName || "",
    reportedUserId: item.reportedUserId || "",
    reportedUserName: item.reportedUserName || "",
    reasons: Array.isArray(item.reasons)
      ? item.reasons.map((r) => String(r))
      : [],
    feedback: item.feedback || "",
    status: item.status || "pending",
    assignedAdminId: item.assignedAdminId || null,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    inPage: item.inPage,
    complianceRuleId: item.complianceRuleId,
    complianceMatchValues: parseComplianceMatchValues(
      item.complianceMatchValues
    ),
    aiAction: parseAiAction(item.aiAction),
    statusLog: parseStatusLog(item.statusLog),
  };
};

const getRowUserId = (complaint) =>
  isGroupedByReporter.value
    ? complaint.reportedUserId
    : complaint.reporterUserId;

const getRowUserName = (complaint) => {
  if (isGroupedByReporter.value) {
    return complaint.reportedUserName || "Unknown";
  }
  if (complaint.reporterType === "system") {
    return "System";
  }
  return complaint.reporterUserName || "Unknown";
};

const parseUserSummary = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const parseDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const calculateAge = (birthday) => {
    if (!birthday) return null;
    const now = new Date();
    let age = now.getFullYear() - birthday.getFullYear();
    const hasHadBirthday =
      now.getMonth() > birthday.getMonth() ||
      (now.getMonth() === birthday.getMonth() &&
        now.getDate() >= birthday.getDate());
    if (!hasHadBirthday) age -= 1;
    return age >= 0 ? age : null;
  };
  const birthdate =
    parseDate(raw.birthdate) || parseDate(raw.birthday) || null;
  return {
    id: raw.id ? String(raw.id) : "",
    name:
      raw.name ||
      raw.fullName ||
      raw.displayName ||
      [raw.firstName, raw.lastName].filter(Boolean).join(" ").trim() ||
      "Unknown",
    email: raw.email || "",
    phone: raw.phone || "",
    status: raw.status || raw.state || "--",
    gender: raw.gender || "--",
    invalidityReasons: raw.invalidityReasons || [],
    verificationStatus: raw.verificationStatus || raw.verifyStatus || "--",
    profileQualityScore: raw.profileQualityScore,
    lastLoginAt: raw.lastLoginDate || raw.lastLoginAt,
    createdAt: raw.createdAt,
    birthday: birthdate,
    age: raw.age ?? calculateAge(birthdate),
    locationLabel: raw.locationLabel,
    address: raw.address,
    atoms: Array.isArray(raw.atoms) ? raw.atoms : [],
  };
};

const localizedValue = (value) =>
  value && typeof value === "object" ? value.en || value.fa || "" : value || "";

const userLocationLabel = computed(() => {
  const address = userSummary.value?.address;
  if (!address) return "";
  return [localizedValue(address.city), localizedValue(address.country)]
    .filter(Boolean)
    .join(", ");
});

const ACTIVE_PLAN_STATUSES = [
  "active",
  "subscription_trialing",
  "subscription_in_grace_period",
];
const activePlans = ref([]);
const isPlanActive = (plan) => {
  if (plan.acquisitionType === "default_plan") return true;
  if (!ACTIVE_PLAN_STATUSES.includes(plan.status)) return false;
  const now = Date.now();
  if (plan.fromDatetime && plan.fromDatetime.getTime() > now) return false;
  if (plan.toDatetime && plan.toDatetime.getTime() <= now) return false;
  return true;
};
const loadActivePlans = async (id) => {
  if (!canViewPay.value) return;
  try {
    const payload = await fetchUserPurchases({ userId: id, page: 1, pageSize: 50 });
    const toDate = (value) => (value ? new Date(value) : null);
    activePlans.value = (payload?.items || [])
      .map((item) => ({
        id: item.id,
        planId: item.planId || "",
        status: item.status || "",
        acquisitionType: item.acquisitionType || "",
        fromDatetime: toDate(item.fromDatetime),
        toDatetime: toDate(item.toDatetime),
      }))
      .filter(isPlanActive);
  } catch (error) {
    console.warn("Failed to fetch active plans for complaints", error);
  }
};
const activePlanLabel = computed(() =>
  activePlans.value.map((plan) => plan.planId || "Plan").join(", ")
);

const loadUserSummary = async () => {
  if (!userId.value) return;
  try {
    const [payload] = await Promise.all([
      fetchUserById(userId.value),
      loadActivePlans(userId.value),
      loadVerificationAttempts(userId.value),
    ]);
    userSummary.value = parseUserSummary(payload);
    selectedUserStatus.value = userSummary.value?.status || "";
  } catch (error) {
    console.warn("Failed to fetch user summary for complaints", error);
  }
};

const loadComplaints = async () => {
  if (!userId.value) return;
  isLoading.value = true;
  errorMessage.value = "";
  statusMessage.value = "";
  try {
    const payload = await fetchComplaintsByUser(userId.value, {
      groupBy: groupBy.value,
    });
    const parsed = Array.isArray(payload)
      ? payload
        .filter((item) => item && typeof item === "object")
        .map((item) => parseComplaint(item))
      : [];
    complaints.value = parsed;
    initialPendingIds.value = new Set(
      parsed.filter((c) => c.status === "pending").map((c) => c.id)
    );
    if (!userSummary.value) {
      await loadUserSummary();
    }
  } catch (error) {
    console.error("Failed to load complaints", error);
    errorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load complaints. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const openMedia = (url) => {
  if (!url) return;
  currentMediaUrl.value = url;
  showMediaModal.value = true;
};

const closeMedia = () => {
  currentMediaUrl.value = "";
  showMediaModal.value = false;
};

const goBack = () => router.push({ name: "Complaints" });

const getMessagesHref = (userIds = []) => {
  const ids = Array.isArray(userIds) ? userIds : [userIds];
  const normalized = ids
    .filter(Boolean)
    .map((v) => (typeof v === "string" ? v : `${v}`));
  const unique = [...new Set(normalized)];
  if (unique.length < 2) return "";
  return router.resolve({
    name: "Messages",
    query: { "participantIds[]": unique },
  }).href;
};

const goToNext = async () => {
  if (isLoadingNext.value) return;
  isLoadingNext.value = true;
  statusMessage.value = "";
  statusType.value = "success";
  try {
    const payload = await fetchComplaints({
      page: 1,
      pageSize: NEXT_PAGE_SIZE,
      groupBy: groupBy.value,
      sortBy: sortByFilter.value,
      status: statusFilter.value,
      assignedAdminIds: assignedAdminIdsFilter.value,
    });
    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    const ids = Array.isArray(rawItems)
      ? rawItems
        .map((item) => item?.user?.id)
        .filter(Boolean)
        .map((id) => String(id))
      : [];
    if (ids.length === 0) {
      statusMessage.value = "No complaints found to navigate.";
      statusType.value = "warning";
      return;
    }
    const currentId = String(userSummary.value?.id || userId.value || "");
    const nextId = ids.find((id) => id !== currentId) || ids[0];
    if (!nextId) {
      statusMessage.value = "No other complaints found.";
      statusType.value = "warning";
      return;
    }
    const segment = groupBy.value === "reporterUserId" ? "reporter" : "reported";
    const query = {};
    if (statusFilter.value) query.status = statusFilter.value;
    if (sortByFilter.value) query.sortBy = sortByFilter.value;
    if (assignedAdminIdsFilter.value?.length) {
      query.assignedAdminIds = assignedAdminIdsFilter.value.map((v) =>
        v === null ? "null" : v
      );
    }
    await router.push({
      path: `/complaints/${segment}/${nextId}`,
      query: Object.keys(query).length ? query : undefined,
    });
  } catch (error) {
    console.error("Failed to navigate to next complaint", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load next complaint.";
    statusType.value = "danger";
  } finally {
    isLoadingNext.value = false;
  }
};

// Snapshot of which complaint IDs were pending when the list was last loaded.
// Resolving/rejecting a complaint keeps it in place (still shown here, just with
// its new status) instead of yanking it into the folded section immediately —
// it only moves there once the page reloads.
const initialPendingIds = ref(new Set());
const byCreatedAtDesc = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
const pendingComplaints = computed(() =>
  complaints.value
    .filter((item) => initialPendingIds.value.has(item.id))
    .sort(byCreatedAtDesc)
);
const nonPendingComplaints = computed(() =>
  complaints.value
    .filter((item) => !initialPendingIds.value.has(item.id))
    .sort(byCreatedAtDesc)
);

const livePendingComplaints = computed(() =>
  complaints.value.filter((item) => item.status === "pending")
);
const pendingCount = computed(() => livePendingComplaints.value.length);
const pendingReportersToWarn = computed(() => {
  const byId = new Map();
  livePendingComplaints.value.forEach((c) => {
    if (c.reporterType !== "system" && c.reporterUserId && !byId.has(c.reporterUserId)) {
      byId.set(c.reporterUserId, c.reporterUserName || "Unknown");
    }
  });
  return [...byId.entries()].map(([id, name]) => ({ id, name }));
});
const visibleComplaints = computed(() => [
  ...pendingComplaints.value,
  ...nonPendingComplaints.value,
]);

const userStatusChanged = computed(
  () => selectedUserStatus.value && selectedUserStatus.value !== userSummary.value?.status
);

const applySummaryParts = computed(() => {
  const parts = [];
  if (userStatusChanged.value) {
    parts.push(`set user status to ${formatStatus(selectedUserStatus.value).toLowerCase()}`);
  }
  if (selectedComplaintStatus.value && pendingCount.value > 0) {
    const verb = selectedComplaintStatus.value === "resolved" ? "resolve" : "reject";
    parts.push(`${verb} ${pendingCount.value} pending complaint${pendingCount.value !== 1 ? "s" : ""}`);
  }
  if (labelNoHumanFace.value && photoAtoms.value.length > 0) {
    parts.push(
      `label ${photoAtoms.value.length} photo${photoAtoms.value.length !== 1 ? "s" : ""} as "no human face"`
    );
  }
  if (requireVerification.value) {
    parts.push("require re-verification");
  }
  if (sendWarningMessage.value) {
    parts.push("send a warning message to reported user");
  }
  if (selectedReporterIdsToWarn.value.length > 0) {
    parts.push(
      `send a warning message to ${selectedReporterIdsToWarn.value.length} reporter${selectedReporterIdsToWarn.value.length !== 1 ? "s" : ""}`
    );
  }
  return parts;
});

const applyActions = async () => {
  if (!userId.value) return;
  if (applySummaryParts.value.length === 0) {
    statusMessage.value = "Select at least one action to apply.";
    statusType.value = "danger";
    return;
  }
  if (selectedComplaintStatus.value && pendingCount.value === 0) {
    statusMessage.value = "No pending complaints to update.";
    statusType.value = "warning";
    return;
  }
  const pendingComplaintIds = complaints.value
    .filter((c) => c.status === "pending")
    .map((c) => c.id)
    .filter(Boolean);
  const complaintReferences = pendingComplaintIds.map((id) => ({
    type: "complaint",
    id,
  }));
  const pendingReporterComplaints = complaints.value.filter(
    (c) => c.status === "pending" && c.reporterType !== "system" && c.reporterUserId
  );
  const reporterIdsToWarn = [
    ...new Set(pendingReporterComplaints.map((c) => c.reporterUserId)),
  ];

  isApplying.value = true;
  statusMessage.value = "";
  try {
    if (userStatusChanged.value) {
      await updateUserStatus(
        userId.value,
        selectedUserStatus.value,
        userStatusComment.value,
        complaintReferences
      );
    }

    if (requireVerification.value) {
      await updateUser(userId.value, {
        verificationStatus: "required",
        verificationStatusReason: "Verification requested from complaint review",
        verificationStatusReferences: complaintReferences,
      });
    }

    if (userStatusChanged.value || requireVerification.value) {
      await loadUserSummary();
    }

    if (selectedComplaintStatus.value && pendingCount.value > 0) {
      const pending = complaints.value.filter(
        (c) => c.status === "pending"
      );
      const updates = await Promise.all(
        pending.map((c) =>
          updateComplaintStatus(
            c.id,
            selectedComplaintStatus.value,
            selectedComplaintStatusComment.value
          ).catch(() => null)
        )
      );
      const updatedList = complaints.value.slice();
      updates.forEach((result) => {
        if (!result) return;
        const normalized = parseComplaint(result);
        const idx = updatedList.findIndex((c) => c.id === normalized.id);
        if (idx !== -1) {
          updatedList[idx] = normalized;
        }
      });
      complaints.value = updatedList;
    }

    if (labelNoHumanFace.value && photoAtoms.value.length > 0) {
      for (const atom of photoAtoms.value) {
        await addMediaLabel(atom.mediaPath, "no_human_face").catch(() => null);
      }
    }

    if (sendWarningMessage.value) {
      await sendUserWarningMessage(
        userId.value,
        pendingComplaintIds,
        COMPLIANCE_ACTION_REASON
      );
    }

    const selectedReporterIdsToWarnNow = reporterIdsToWarn.filter((id) =>
      selectedReporterIdsToWarn.value.includes(id)
    );
    if (selectedReporterIdsToWarnNow.length > 0) {
      for (const reporterId of selectedReporterIdsToWarnNow) {
        const complaintIdsForReporter = pendingReporterComplaints
          .filter((c) => c.reporterUserId === reporterId)
          .map((c) => c.id)
          .filter(Boolean);
        await sendUserWarningMessage(
          reporterId,
          complaintIdsForReporter,
          COMPLIANCE_ACTION_REASON
        );
      }
    }

    statusMessage.value = "Actions applied successfully.";
    statusType.value = "success";
    userStatusComment.value = COMPLIANCE_ACTION_REASON;
    selectedComplaintStatus.value = "";
    selectedComplaintStatusComment.value = "";
    labelNoHumanFace.value = false;
    requireVerification.value = false;
    sendWarningMessage.value = false;
    selectedReporterIdsToWarn.value = [];
    detailsPanelRef.value?.refresh();
  } catch (error) {
    console.error("Failed to apply actions", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to apply actions. Please try again.";
    statusType.value = "danger";
  } finally {
    isApplying.value = false;
  }
};
const openStatusChangeModal = (complaint, status) => {
  if (!complaint?.id || !status || complaint.status === status) return;
  if (isStatusLocked(complaint.status, status)) {
    statusMessage.value = "Resolved or rejected complaints cannot move back to pending.";
    statusType.value = "warning";
    return;
  }
  statusChangeTarget.value = { complaint, status };
  statusChangeComment.value = "";
};

const closeStatusChangeModal = () => {
  statusChangeTarget.value = null;
  statusChangeComment.value = "";
};

const confirmStatusChange = async () => {
  const target = statusChangeTarget.value;
  if (!target) return;
  const { complaint, status } = target;
  isUpdatingStatus.value = true;
  try {
    const updated = await updateComplaintStatus(
      complaint.id,
      status,
      statusChangeComment.value
    );
    const normalized = parseComplaint(updated);
    complaints.value = complaints.value.map((item) =>
      item.id === normalized.id ? normalized : item
    );
    statusMessage.value = "Complaint status updated";
    statusType.value = "success";
    closeStatusChangeModal();
  } catch (error) {
    console.error("Failed to update complaint status", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to update complaint status.";
    statusType.value = "danger";
  } finally {
    isUpdatingStatus.value = false;
  }
};

const rerunAi = async (complaint) => {
  if (!complaint?.id || rerunAiLoadingFor.value) return;
  rerunAiLoadingFor.value = complaint.id;
  try {
    const updated = await rerunComplaintAi(complaint.id);
    const normalized = parseComplaint(updated);
    complaints.value = complaints.value.map((item) =>
      item.id === normalized.id ? normalized : item
    );
    statusMessage.value = "AI review rerun successfully";
    statusType.value = "success";
  } catch (error) {
    console.error("Failed to rerun AI for complaint", error);
    statusMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to rerun AI for complaint.";
    statusType.value = "danger";
  } finally {
    rerunAiLoadingFor.value = null;
  }
};

onMounted(async () => {
  loadAdmins();
  await Promise.all([loadComplaints(), loadUserSummary()]);
});

watch(
  () => [userId.value, groupBy.value],
  async ([nextId, nextGroup], [prevId, prevGroup]) => {
    if (nextId === prevId && nextGroup === prevGroup) return;
    await Promise.all([loadComplaints(), loadUserSummary()]);
  }
);
</script>

<template>
  <div class="pb-4 container-fluid">
    <div v-if="isLoading" class="card">
      <div class="card-body text-center py-5">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 mb-0 text-sm text-secondary">Loading complaints...</p>
      </div>
    </div>

    <div v-else-if="errorMessage" class="card">
      <div class="card-body text-center py-5">
        <div class="icon icon-shape bg-gradient-danger text-white shadow text-center border-radius-md mx-auto">
          <i class="ni ni-fat-remove"></i>
        </div>
        <p class="mt-3 text-sm">{{ errorMessage }}</p>
        <button class="btn btn-sm btn-success" type="button" @click="loadComplaints">
          Try again
        </button>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-12 col-xl-8">
        <div class="card shadow-sm mb-3">
          <div class="card-header pb-0 border-0">
            <div class="d-flex flex-column gap-2">
              <div class="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                <button class="btn btn-xs btn-outline-secondary mb-0" type="button" @click="goBack">
                  <i class="fas fa-arrow-left me-2"></i>
                  List
                </button>
                <button class="btn btn-xs btn-outline-secondary mb-0" type="button"
                  :disabled="isLoadingNext || isLoading" @click="goToNext">
                  <i class="fas fa-arrow-right me-2"></i>
                  {{ isLoadingNext ? "Loading..." : "Next" }}
                </button>
                <button class="btn btn-xs btn-outline-success mb-0" type="button" @click="loadComplaints">
                  <i class="fas fa-sync me-2"></i>
                  {{ isLoading ? "Refreshing..." : "" }}
                </button>
              </div>
            </div>
            <div v-if="userSummary" class="mt-2">
              <div class="overflow-auto">
                <div class="d-flex align-items-center gap-3 flex-nowrap" style="min-width: max-content;">
                  <router-link v-if="userSummary.id" :to="`/users/${userSummary.id}`"
                    class="text-sm fw-bold text-nowrap">{{ userSummary.name }}</router-link>
                  <span v-else class="text-sm fw-bold text-nowrap">{{ userSummary.name }}</span>
                  <span class="badge badge-sm text-nowrap"
                    :class="userSummary.status === 'active' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                    {{ formatStatus(userSummary.status) }}
                  </span>
                  <span class="badge badge-sm text-nowrap"
                    :class="userSummary.verificationStatus === 'verified' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                    {{ formatStatus(userSummary.verificationStatus) }}
                  </span>
                  <span v-if="userSummary.age" class="text-xs text-secondary text-nowrap">{{ userSummary.age }}
                    y/o</span>
                  <span v-if="userLocationLabel" class="text-xs text-secondary text-nowrap">{{ userLocationLabel
                  }}</span>
                  <span v-if="activePlanLabel" class="text-xs text-secondary text-nowrap">{{ activePlanLabel }}</span>
                </div>
              </div>
              <div v-if="userBio" class="text-xs text-secondary mt-1 text-wrap bio-text">
                Bio: <span>{{ userBio }}</span>
              </div>
              <div v-if="photoAtoms.length || verificationPhotoItems.length" class="d-flex gap-2 flex-nowrap mt-2">
                <img v-for="photo in photoAtoms" :key="photo.id || photo.mediaPath" :src="getMediaUrl(photo.mediaPath)"
                  alt="profile photo" class="rounded border flex-shrink-0"
                  style="width: 70px; height: 70px; object-fit: cover; cursor: zoom-in"
                  @click="openMedia(getMediaUrl(photo.mediaPath))" />
                <div v-for="item in verificationPhotoItems" :key="item.key" class="position-relative flex-shrink-0"
                  style="width: 70px; height: 70px;">
                  <img v-if="item.kind === 'media'" :src="item.url" alt="verification photo"
                    class="rounded border w-100 h-100" style="object-fit: cover; cursor: zoom-in"
                    @click="openMedia(item.url)" />
                  <button v-else type="button" class="btn btn-xs btn-outline-success w-100 h-100 m-0 p-0"
                    @click="openVerificationPhoto(item.attemptId, item.index)">
                    View
                  </button>
                  <span class="badge bg-gradient-success position-absolute"
                    style="bottom: 2px; left: 2px; right: 2px; font-size: 8px; padding: 1px 2px; line-height: 1.2;">
                    Verification photo
                  </span>
                </div>
              </div>
            </div>
            <div v-if="statusMessage" class="alert mt-3" :class="`alert-${statusType}`">
              {{ statusMessage }}
            </div>
          </div>

          <div class="card-body">
            <div v-if="assignError" class="alert alert-danger text-sm py-2 mb-3">{{ assignError }}</div>
            <div v-if="canEditUser || canEditComplaint || canEditMedia" class="p-2 my-3 bg-gray-100 border rounded">
              <div class="row g-3">
                <div class="col-12 col-md-6" v-if="!isGroupedByReporter && canEditUser">
                  <label class="form-label text-sm fw-semibold mb-1">User status</label>
                  <div class="d-flex flex-wrap">
                    <button v-for="status in userStatusOptions" :key="status" type="button" class="m-0"
                      :class="userStatusButtonClass(status, selectedUserStatus)" :disabled="isApplying"
                      @click="selectedUserStatus = status">
                      {{ formatStatus(status) }}
                    </button>
                  </div>
                  <input v-if="userStatusChanged" v-model="userStatusComment" type="text"
                    class="form-control form-control-sm mt-2" :disabled="isApplying"
                    placeholder="Comment for this status change">
                </div>
                <div class="col-12 col-md-6" v-if="canEditComplaint">
                  <label class="form-label text-sm fw-semibold mb-1">
                    Pending complaints
                    <span class="text-secondary fw-normal">({{ pendingCount }} pending)</span>
                  </label>
                  <div class="d-flex flex-wrap">
                    <button v-for="status in bulkComplaintOptions" :key="status" type="button" class="m-0"
                      :class="bulkStatusButtonClass(status, selectedComplaintStatus)"
                      :disabled="isApplying || pendingCount === 0"
                      @click="selectedComplaintStatus = selectedComplaintStatus === status ? '' : status">
                      {{ formatStatus(status) }}
                    </button>
                  </div>
                  <input v-if="selectedComplaintStatus" v-model="selectedComplaintStatusComment" type="text"
                    class="form-control form-control-sm mt-2" :disabled="isApplying"
                    placeholder="Add a comment for this status change (optional)">
                </div>
                <div class="col-12" v-if="!isGroupedByReporter && (canEditMedia || canEditUser)">
                  <label class="form-label text-sm fw-semibold mb-1">Additional actions</label>
                  <div class="d-flex flex-column gap-2">
                    <div class="form-check form-switch mb-0" v-if="canEditMedia">
                      <input id="apply-label-photos" class="form-check-input" type="checkbox" v-model="labelNoHumanFace"
                        :disabled="isApplying || photoAtoms.length === 0">
                      <label class="form-check-label text-sm" for="apply-label-photos">
                        Label {{ photoAtoms.length }} photo{{ photoAtoms.length !== 1 ? 's' : '' }} as "no human face"
                      </label>
                    </div>
                    <div class="form-check form-switch mb-0" v-if="canEditUser">
                      <input id="apply-require-verification" class="form-check-input" type="checkbox"
                        v-model="requireVerification" :disabled="isApplying">
                      <label class="form-check-label text-sm" for="apply-require-verification">
                        Require re-verification
                      </label>
                    </div>
                    <div class="form-check form-switch mb-0" v-if="canEditUser">
                      <input id="apply-send-warning" class="form-check-input" type="checkbox"
                        v-model="sendWarningMessage" :disabled="isApplying">
                      <label class="form-check-label text-sm" for="apply-send-warning">
                        Send a warning message to reported user
                      </label>
                    </div>
                    <div v-if="canEditUser && pendingReportersToWarn.length > 0">
                      <p class="text-sm mb-1">Send warning to reporters</p>
                      <div v-for="reporter in pendingReportersToWarn" :key="reporter.id"
                        class="form-check d-flex align-items-center gap-2 mb-1 ps-0">
                        <input :id="`apply-send-warning-reporter-${reporter.id}`" class="form-check-input mt-0 ms-0"
                          type="checkbox" v-model="selectedReporterIdsToWarn" :value="reporter.id"
                          :disabled="isApplying">
                        <router-link :to="`/users/${reporter.id}`" class="text-sm">
                          {{ reporter.name }}
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="applySummaryParts.length" class="text-xs text-secondary mt-3 pt-3 border-top">
                <strong>This will:</strong> {{ applySummaryParts.join(", ") }}.
              </div>
              <div class="mt-3 d-flex flex-row gap-2">
                <button class="btn btn-sm btn-success mb-0" type="button"
                  :disabled="isApplying || applySummaryParts.length === 0" @click="applyActions">
                  <span v-if="isApplying" class="spinner-border spinner-border-sm me-2"></span>
                  Apply actions
                </button>
                <MediaComplianceRuleWidget :photos="photoAtoms" :user-id="userSummary?.id"
                  :user-name="userSummary?.name" @status="handleMediaComplianceStatus" />
              </div>
            </div>
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Report</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      AI Decision
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="complaints.length === 0">
                    <td colspan="3" class="text-center text-sm text-secondary py-4">No complaints found.</td>
                  </tr>
                  <template v-else>
                    <template v-for="(complaint, index) in visibleComplaints"
                      :key="complaint.id || complaint.createdAt">
                      <tr :class="{ 'bg-light': index < pendingComplaints.length }">
                        <td class="text-sm report-cell text-wrap">
                          <div class="d-flex flex-column gap-2">
                            <div>
                              <router-link v-if="getRowUserId(complaint)"
                                :to="`/complaints/reported/${getRowUserId(complaint)}`"
                                class="btn btn-link p-0 text-start text-sm font-weight-bold mb-0">
                                {{ getRowUserName(complaint) }}
                              </router-link>
                              <span v-else class="text-sm font-weight-bold">
                                {{ getRowUserName(complaint) }}
                              </span>
                            </div>
                            <div class="d-flex flex-column gap-2">
                              <span v-if="complaint.reasons.length"
                                v-text="complaint.reasons.map((r) => safeText(r)).join(', ')" />
                              <span v-if="complaint.feedback" class="text-secondary text-xs text-wrap">
                                Feedback: {{ safeText(complaint.feedback) }}
                              </span>
                              <span v-if="complaint.inPage" class="text-secondary text-xs text-wrap">
                                <small>Page: {{ safeText(complaint.inPage) }}</small>
                              </span>
                              <div v-if="complaint.complianceMatchValues?.length" class="bg-light border rounded p-2">
                                <p class="text-xxs text-secondary mb-1">Compliance matches</p>
                                <div class="d-flex flex-column gap-1">
                                  <div v-for="(match, matchIndex) in complaint.complianceMatchValues"
                                    :key="`${complaint.id || complaint.createdAt}-match-${matchIndex}`" class="text-xs">
                                    <template v-if="match.type === 'StringValue'">
                                      <span class="badge bg-light text-dark border me-2 text-xxs">
                                        {{ match.field || "field" }}
                                      </span>
                                      <span class="text-secondary">{{ safeText(match.value) || "--" }}</span>
                                    </template>
                                    <template v-else-if="match.type === 'MediaValue'">
                                      <span class="badge bg-light text-dark border me-2 text-xxs">
                                        {{ match.field || "media" }}
                                      </span>
                                      <template v-if="match.mediaUrl && match.mediaType === 'voice'">
                                        <audio :src="match.mediaUrl" controls class="w-100 mt-1"></audio>
                                      </template>
                                      <template v-else-if="match.mediaUrl">
                                        <img :src="match.mediaUrl" alt="compliance media" class="rounded border"
                                          style="width: 200px; height: 200px; object-fit: cover; cursor: zoom-in"
                                          @click="openMedia(match.mediaUrl)" />
                                      </template>
                                      <span v-else class="text-secondary">Path: {{ safeText(match.mediaPath) }}</span>
                                      <span v-if="match.mediaType" class="text-secondary ms-1">({{ match.mediaType
                                      }})</span>
                                      <div v-if="match.labels?.length" class="text-xxs text-secondary mt-1">
                                        <span v-for="(label, labelIndex) in match.labels"
                                          :key="`${matchIndex}-label-${labelIndex}`" class="me-2">
                                          {{ formatComplianceLabel(label) }}
                                        </span>
                                      </div>
                                      <div v-if="match.references?.length" class="d-flex flex-wrap gap-1 mt-1">
                                        <template v-for="(reference, refIndex) in match.references"
                                          :key="`${matchIndex}-ref-${refIndex}`">
                                          <router-link v-if="mediaReferenceUrl(reference, match.mediaUserId)"
                                            :to="mediaReferenceUrl(reference, match.mediaUserId)"
                                            class="badge bg-light text-dark border text-xs fw-light text-decoration-none">
                                            {{ mediaReferenceLabel(reference) }}
                                          </router-link>
                                        </template>
                                      </div>
                                    </template>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="text-xs text-secondary">
                              <span>
                                <small>
                                  Created: {{ formatDate(complaint.createdAt) }}
                                </small>
                              </span>
                              <span class="ms-2" v-if="complaint.updatedAt">
                                <small>
                                  Updated: {{ formatDate(complaint.updatedAt) }}
                                </small>
                              </span>
                              <div v-if="complaint.statusLog?.length" class="mt-2">
                                <p class="text-xxs text-secondary mb-1 text-uppercase">Status log</p>
                                <div v-for="(entry, i) in complaint.statusLog" :key="i"
                                  class="border-start border-2 ps-2 mb-1"
                                  :class="entry.status === 'resolved' ? 'border-success' : entry.status === 'rejected' ? 'border-danger' : 'border-warning'">
                                  <div class="d-flex align-items-center gap-1 flex-wrap">
                                    <span :class="statusBadgeClass(entry.status)" style="font-size: 0.6rem;">
                                      {{ formatStatus(entry.status) }}
                                    </span>
                                    <span class="text-secondary">
                                      by {{ entry.actorLabel }}
                                      <small class="text-secondary" style="font-size: 0.65rem;">
                                        at {{ formatDate(entry.changedAt) }}
                                      </small>
                                    </span>
                                  </div>
                                  <div v-if="entry.comment" class="text-secondary fst-italic"
                                    style="font-size: 0.65rem;">
                                    {{ entry.comment }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td class="text-center">
                          <span v-if="complaint.reporterType === 'system' && !complaint.complianceRuleId"
                            class="text-xs text-secondary fst-italic">
                            Not applicable
                          </span>
                          <template v-else>
                            <div v-if="complaint.aiAction?.action" class="d-flex flex-column align-items-center">
                              <span :class="aiDecisionBadgeClass(complaint.aiAction.action)">
                                {{ formatAiDecisionAction(complaint.aiAction.action) }}
                              </span>
                              <span v-if="complaint.aiAction.insight" class="text-xxs text-secondary text-wrap mt-1">
                                {{ safeText(complaint.aiAction.insight) }}
                              </span>
                            </div>
                            <span v-else class="text-xs text-secondary">--</span>
                            <button v-if="canEditComplaint" type="button"
                              class="btn btn-icon-only btn-sm btn-outline-secondary mb-0 mt-1"
                              :disabled="!(complaint.reporterUserId || complaint.complianceRuleId) || rerunAiLoadingFor === complaint.id"
                              :title="!(complaint.reporterUserId || complaint.complianceRuleId) ? 'Reporter user is required for AI review.' : (complaint.aiAction?.action ? 'Rerun AI' : 'Run AI')"
                              @click="rerunAi(complaint)">
                              <span v-if="rerunAiLoadingFor === complaint.id"
                                class="spinner-border spinner-border-sm"></span>
                              <i v-else class="fas fa-sync"></i>
                            </button>
                          </template>
                        </td>
                        <td class="text-center">
                          <div class="d-flex flex-column align-items-center gap-2">
                            <div v-if="canEditComplaint" class="d-flex justify-content-center flex-wrap">
                              <button
                                v-for="status in complaintStatusOptions.filter((s) => !isStatusLocked(complaint.status, s))"
                                :key="status" type="button" :class="statusButtonClass(status, complaint.status)"
                                @click="openStatusChangeModal(complaint, status)">
                                {{ formatStatus(status) }}
                              </button>
                            </div>
                            <div class="d-flex justify-content-center flex-wrap">
                              <a v-if="complaint.reporterUserId && complaint.reportedUserId"
                                class="btn btn-sm btn-outline-info m-1"
                                :href="getMessagesHref([complaint.reportedUserId, complaint.reporterUserId])">
                                Messages
                              </a>
                            </div>
                            <div v-if="canEditComplaint" class="position-relative">
                              <button class="btn btn-sm btn-outline-secondary m-1" :disabled="assignLoading"
                                @click="toggleAssignMenu(complaint.id)">
                                👤 {{ adminLabel(complaint.assignedAdminId) }}
                              </button>
                              <div v-if="assignMenuOpenFor === complaint.id"
                                class="position-absolute bg-white shadow-sm rounded-2 border py-1 assign-dropdown">
                                <button class="dropdown-item text-sm py-1 px-2"
                                  :class="{ 'fw-bold': !complaint.assignedAdminId }"
                                  @click="assignComplaintToAdmin(complaint, null)">
                                  Unassigned
                                </button>
                                <div v-if="admins.length" class="dropdown-divider my-1"></div>
                                <button v-for="admin in admins" :key="admin.id" class="dropdown-item text-sm py-1 px-2"
                                  :class="{ 'fw-bold': complaint.assignedAdminId === admin.id }"
                                  @click="assignComplaintToAdmin(complaint, admin.id)">
                                  {{ admin.name }}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-4 mb-4">
        <UserDetailsPanel ref="detailsPanelRef" :user-id="userId" empty-message="No user selected." />
      </div>
    </div>

    <div v-if="statusChangeTarget" class="modal fade show d-block" tabindex="-1" style="background: rgba(0, 0, 0, 0.5);"
      @click.self="closeStatusChangeModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title mb-0">
              {{ formatStatus(statusChangeTarget.status) }} complaint
            </h6>
            <button type="button" class="btn-close" @click="closeStatusChangeModal"></button>
          </div>
          <div class="modal-body">
            <label class="form-label text-sm">Comment (optional)</label>
            <textarea v-model="statusChangeComment" class="form-control" rows="3"
              placeholder="Add a comment for this status change..."></textarea>
          </div>
          <div class="modal-footer gap-1">
            <button type="button" class="btn btn-outline-secondary m-0" @click="closeStatusChangeModal">
              Cancel
            </button>
            <button type="button" class="btn btn-primary m-0" :disabled="isUpdatingStatus" @click="confirmStatusChange">
              <span v-if="isUpdatingStatus" class="spinner-border spinner-border-sm me-1"></span>
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showMediaModal && currentMediaUrl" class="modal fade show d-block" tabindex="-1"
      style="background: rgba(0, 0, 0, 0.75);" @click.self="closeMedia">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content bg-transparent border-0 shadow-none">
          <div class="modal-body text-center p-0">
            <img :src="currentMediaUrl" alt="media preview" class="img-fluid rounded" />
          </div>
          <div class="modal-footer justify-content-center border-0">
            <button type="button" class="btn btn-light" @click="closeMedia">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.assign-dropdown {
  top: 100%;
  left: 0;
  z-index: 100;
  min-width: 160px;
  max-height: 260px;
  overflow-y: auto;
}

.report-cell {
  max-width: 420px;
  overflow-wrap: anywhere;
}

.bio-text {
  max-width: 100vw;
  overflow-wrap: anywhere;
}
</style>
